import React, { FC, useCallback } from 'react';
import { Box, Typography, Paper, makeStyles, alpha, Button } from '@material-ui/core';
import { Room } from '@/types/Room';
import { Procedure } from '@/types/Procedure';
import StaffShifts from '../staff/StaffShifts';
import { Anesthesiologist } from '../anesthesiologist/Personnel';
import { Anesthesiologist as AnesthesiologistRoom } from '../anesthesiologist/PersonnelRoom';
import { format, isToday } from 'date-fns';
import {
  fullNameToLastNameRest,
  fullNameToDrLastName,
  fullNameToDrLastNameMiddleEast,
  getDelayInMinutes,
  getOrLateEntryInMinutes,
  isDimmed,
  isNoShow,
  NormalizedStatus,
  normalizeStatus,
  shouldHighlightPhysicianInOr,
} from '../shared/procedureUtils';
import { StaffShiftRoomContext } from '../staff/StaffShiftContext';
import { StaffSlotRoomContext } from '../staff/StaffSlotContext';
import { StaffSlotProcedureContext } from '../staff/StaffSlotContext';
import { PrimarySpecialty } from '@/types/StaffMember';
import StaffShiftMenu from '../staff/StaffShiftMenu';
import { useScope } from '@/hooks/useScope';
import { useStylesOverview } from '../overview/Overview';
import { useStatusDisplay } from '@/hooks/useStatusColor';
import { getInOrStatusLabel } from '../statusUtils';
import { getRoomTime } from '@/util/procedureEvents';
import { unparsedAllergiesSummary } from '@/allergies/AllergiesView';
import { useScreenConfiguration } from '@/contexts/ScreenConfigurationContext';
import { ROOM_TYPES } from '@/components/entities/room/enums';
import { isPreOpAsDPUOptimized } from '../../tablet/utils';
import isMiddleEast from '@/util/isMiddleEast';
import MultipleSelectionCheckbox from '../staff/MultipleSelectionCheckbox';
import ProcedureColorSelector from '../overview/ProcedureColorSelector';
import { Physician } from '../anesthesiologist/Personnel';
import { PatientNameWithSession } from '../Patient';
import { VendorProcedureProvider } from '../vendor/context/VendorProcedureContext';
import Vendors from '../vendor/Vendors';
import VendorMenu from '../vendor/VendorMenu';
import { STATUS_LABELS } from '@/components/entities/patient/enums';
import clsx from 'clsx';
import { Procedure as ProcedureT } from '../../../../../types/Procedure';
import { Time } from '../../../../entities/schedule/util/time';
import { StaffShiftProcedureContext } from '../staff/StaffShiftContext';

const useStyles = makeStyles(theme => ({
  procedureCard: {
    marginBottom: theme.spacing(0.5),
    padding: theme.spacing(0.5),
    borderLeft: '4px solid',
    backgroundColor: theme.palette.background.paper,
    '&:hover': {
      backgroundColor: theme.palette.background.default,
    },
    transition: 'background-color 150ms ease-in-out',
  },
  dimmed: {
    opacity: 0.3,
  },
  cardContent: {
    display: 'flex',
    gap: theme.spacing(1),
  },
  leftSection: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: theme.spacing(1),
    flex: 4,
  },
  rightSection: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: theme.spacing(0.5),
    flex: 1,
  },
  mainInfo: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.25),
    flex: 1,
    minWidth: 0, // Enable text truncation
  },
  timeAndStatus: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  notes: {
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
    fontSize: '0.75rem',
    alignItems: 'center',
  },
  staffSection: {
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: theme.spacing(0.5),
    alignItems: 'center',
  },
  statusChip: {
    fontWeight: 'bold',
    color: 'white',
    padding: '2px 8px',
    borderRadius: '4px',
    fontSize: '0.75rem',
    whiteSpace: 'nowrap',
  },
  infoRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    flexWrap: 'wrap',
  },
  roomSection: {
    marginBottom: theme.spacing(2),
    padding: theme.spacing(1.5),
    backgroundColor: theme.palette.background.paper,
    borderRadius: theme.shape.borderRadius,
  },
  staffWrapper: {
    display: 'flex',
    flexDirection: 'column',
    marginBottom: theme.spacing(1),
  },
  roomHeader: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: theme.spacing(1.5),
    position: 'sticky',
    top: 0,
    backgroundColor: theme.palette.background.paper,
    zIndex: 2,
  },
  checkboxContainer: {
    backgroundColor: alpha(theme.palette.text.primary, 0.1),
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    marginRight: theme.spacing(1),
  },
  helpContainer: {
    border: '3px solid transparent',
    '&.help': {
      animation: `$emphasize 1.2s ease-in alternate infinite`,
    },
  },
  '@keyframes emphasize': {
    '0%': {
      borderColor: 'rgba(255, 0, 0, 0)',
      boxShadow: '0 0 4px rgba(255, 0, 0, 0)',
    },
    '100%': {
      borderColor: 'rgba(255, 0, 0, 1)',
      boxShadow: '0 0 30px rgba(255, 0, 0, 1)',
    },
  },
  truncate: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  smallText: {
    fontSize: '0.875rem',
  },
  noteText: {
    fontSize: '0.75rem',
    lineHeight: 1.2,
  },
  pageContainer: {
    height: '100vh',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
  },
  scrollContainer: {
    flex: 1,
    overflow: 'auto',
  },
  contentContainer: {
    padding: theme.spacing(2),
  },
  physicianInfo: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    fontSize: '0.875rem',
  },
  physicianDot: {
    width: 8,
    height: 8,
    borderRadius: '50%',
    flexShrink: 0,
  },
  patientInfo: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  personnel: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  timeText: {
    fontSize: '1.1rem',
    fontWeight: 600,
    color: theme.palette.text.primary,
    letterSpacing: '0.5px',
  },
  emptyColumn: {
    display: 'flex',
    flexDirection: 'column',
    margin: 'auto 0',
    textAlign: 'center',
    opacity: 0.5,
  },
}));

interface ScheduleListViewProps {
  operationRooms: Room[];
  date: Date;
  isKiosk?: boolean;
  isFrontDesk?: boolean;
  editableStaff?: boolean;
  canUseScheduleViewProcedure?: boolean;
  isPowerUser?: boolean;
  setEditProcedure?: (procedure: Procedure | null) => void;
  showBedNumber?: boolean;
  scheduleStaffList?: any;
  showOverlay?: boolean;
  editMode?: boolean;
  openProcedureForm?: (
    procedure: ProcedureT | undefined | null,
    operationRooms: Room[],
    room: Room,
    date: Date,
    timeRange?: {
      startTime: Time;
      duration: number;
    }
  ) => void;
  condensView?: boolean;
  unassignedProcedures?: Procedure[];
  showGraphs?: boolean;
  isAnesthesiologistSchedule?: boolean;
  secondaryRoomAnesthesiologist?: boolean;
  autoDischarge?: boolean;
}

const ScheduleListView: FC<ScheduleListViewProps> = ({
  operationRooms,
  date,
  isKiosk,
  isFrontDesk,
  editableStaff,
  canUseScheduleViewProcedure,
  isPowerUser,
  setEditProcedure,
  showBedNumber,
  showOverlay = false,
  editMode,
  openProcedureForm,
  condensView,
  isAnesthesiologistSchedule,
  secondaryRoomAnesthesiologist,
  autoDischarge,
}) => {
  const classes = useStyles();
  const overviewClasses = useStylesOverview();
  const scope = useScope();
  const hospitalId = scope?.hospital?.id;
  const { getColor, getContourStyle, getFlashingContourColor } = useStatusDisplay('schedule');
  const { hideNotes } = useScreenConfiguration();
  const canAddStaff = !isKiosk && editableStaff;
  const editableAnesthesiologist = !isKiosk && !isFrontDesk && editableStaff;
  const createProcedureScheduleModule = scope?.hospital?.modules?.createProcedureSchedule;

  const handleProcedureClick = useCallback(
    (procedure: Procedure, room: Room) => {
      if (canUseScheduleViewProcedure) {
        if (createProcedureScheduleModule && openProcedureForm) {
          openProcedureForm(procedure, operationRooms, room, date);
        } else {
          setEditProcedure?.(procedure);
        }
      }
    },
    [
      canUseScheduleViewProcedure,
      createProcedureScheduleModule,
      openProcedureForm,
      operationRooms,
      date,
      setEditProcedure,
    ]
  );

  const renderProcedure = (procedure: Procedure, room: Room) => {
    if (!hospitalId) return null;

    const normalizedStatus = normalizeStatus(
      procedure?.patient?.status,
      procedure?.patient?.room,
      isMiddleEast(hospitalId)
    );
    const status = procedure?.patient?.id ? normalizedStatus : 'PreAdm';
    const isCanceled = procedure.isCanceled;
    const noShow = isNoShow(procedure);
    const isInactive = isDimmed(procedure);
    const statusColor = getColor(status, hospitalId, '#00A7F7'); // Light blue default for status chip
    const procedureColor =
      procedure?.color || getColor(status, hospitalId, null) || getFlashingContourColor(status) || undefined; // No default color for procedure border
    const roomTime = getRoomTime(procedure);
    const isWaiting = [ROOM_TYPES.WAITING_ROOM, ROOM_TYPES.PRE_OP].includes(procedure?.patient?.room?.type);
    const orLateInMinutes = getOrLateEntryInMinutes(procedure, isMiddleEast(hospitalId));
    const delayInMinutes = getDelayInMinutes(procedure);
    const isHoldProcedure = Boolean(procedure?.patient?.events?.holdProcedureAt);
    const isBlockNerve = Boolean(procedure?.patient?.events?.blockNerveActiveAt);
    const isHelp = Boolean(procedure?.patient?.events?.helpActiveAt);
    const isFlipRoom = Boolean(procedure?.patient?.events?.flipRoomActiveAt);
    const isDPU = isPreOpAsDPUOptimized(procedure?.patient?.room?.type, procedure?.patient?.events);
    const allergies = unparsedAllergiesSummary(procedure.allergies);
    const physicianName = isMiddleEast(hospitalId)
      ? fullNameToDrLastNameMiddleEast(procedure?.physician?.name)
      : fullNameToDrLastName(procedure?.physician?.name);
    const physicianColor = procedure?.physician?.color || '#888888';
    const anesthesiologistName = isMiddleEast(hospitalId)
      ? fullNameToDrLastName(procedure?.anesthesiologist?.name)
      : fullNameToLastNameRest(procedure?.anesthesiologist?.name);
    const anesthesiaType = procedure?.anesthesiaType;
    const vendorColor = 'default';

    const highlightPhysician = shouldHighlightPhysicianInOr({
      orId: procedure?.orId,
      physicianCurrentRoomId: procedure?.physician?.currentRoomId,
      patientRoomId: procedure?.patient?.room?.id,
      status: normalizedStatus,
      requirePatientInOr: true,
    });

    const hasNotes =
      procedure.preOpNote ||
      procedure.anesthesiologistNotes ||
      procedure.orNotes ||
      (!hideNotes && procedure.notes) ||
      allergies;

    return (
      <Paper
        key={procedure.id}
        className={clsx(classes.procedureCard, {
          [classes.dimmed]: isInactive,
        })}
        style={{
          borderLeftColor: procedureColor,
          ...(!isHelp && procedureColor ? getContourStyle(status, procedureColor) : undefined),
        }}
        onClick={() => handleProcedureClick(procedure, room)}
      >
        <Box className={classes.helpContainer + (isHelp && !isInactive ? ' help' : '')}>
          <Box className={classes.cardContent}>
            <Box className={classes.leftSection}>
              {canAddStaff && (
                <Box className={classes.checkboxContainer}>
                  <MultipleSelectionCheckbox procedureId={procedure.id} />
                  <ProcedureColorSelector procedureId={procedure.id} color={procedureColor} />
                </Box>
              )}

              <Box className={classes.mainInfo}>
                <Box className={classes.infoRow}>
                  <Typography variant="body1" className={classes.timeText}>
                    {procedure.startTimeText} - {procedure.endTimeText}
                  </Typography>
                  <Typography
                    component="span"
                    className={classes.statusChip}
                    style={{ backgroundColor: statusColor || undefined }}
                  >
                    {normalizedStatus === NormalizedStatus.In_OR
                      ? getInOrStatusLabel(procedure?.patient?.status) ||
                        STATUS_LABELS[procedure?.patient?.status] ||
                        'OR'
                      : isCanceled
                        ? 'Canceled'
                        : noShow
                          ? 'NoShow'
                          : STATUS_LABELS[status] || status}
                  </Typography>
                  {isFlipRoom && (
                    <Typography component="span" className={classes.statusChip} style={{ backgroundColor: '#6c9911' }}>
                      Flip Room
                    </Typography>
                  )}
                  <PatientNameWithSession
                    {...{
                      id: procedure?.patient?.id ?? procedure?.patientId,
                      name: procedure.patientName,
                      age: procedure.patientAge,
                      sex: procedure.patientSex,
                      hospitalId,
                      inline: true,
                      isPatientIconVisible: false,
                      condensView: false,
                      isTimeline: false,
                      compactInitials: false,
                      scheduleIsKiosk: isKiosk,
                    }}
                  />
                </Box>

                <Box className={classes.infoRow}>
                  <Box className={classes.personnel}>
                    <Physician
                      physicianName={physicianName}
                      color={physicianColor}
                      inline
                      highlight={highlightPhysician}
                    />
                    <Anesthesiologist
                      date={date}
                      procedureId={procedure.id}
                      editableAnesthesiologist={!isKiosk && !isFrontDesk && editableStaff}
                      anesthesiologistName={anesthesiologistName}
                      anesthesiaType={anesthesiaType}
                      inline
                      style={{ fontWeight: 400 }}
                      condensView={false}
                    />
                  </Box>
                  <Typography variant="body2" className={`${classes.truncate} ${classes.smallText}`}>
                    {procedure.procedureType}
                    {procedure.procedureMod && ` (${procedure.procedureMod})`}
                  </Typography>
                  {showBedNumber && procedure.patient?.bed && (
                    <Typography variant="body2" className={classes.smallText}>
                      Bed: {procedure.patient.bed}
                    </Typography>
                  )}
                </Box>

                {hasNotes && (
                  <Box className={classes.notes}>
                    {procedure.preOpNote && (
                      <Typography variant="body2" className={classes.noteText} color="secondary">
                        Pre-Op: {procedure.preOpNote}
                      </Typography>
                    )}
                    {procedure.anesthesiologistNotes && (
                      <Typography variant="body2" className={classes.noteText} color="primary">
                        Anest: {procedure.anesthesiologistNotes}
                      </Typography>
                    )}
                    {procedure.orNotes && (
                      <Typography variant="body2" className={classes.noteText} color="textSecondary">
                        OR: {procedure.orNotes}
                      </Typography>
                    )}
                    {!hideNotes && procedure.notes && (
                      <Typography variant="body2" className={classes.noteText} color="textSecondary">
                        Notes: {procedure.notes}
                      </Typography>
                    )}
                    {allergies && (
                      <Typography variant="body2" className={classes.noteText} color="error">
                        Allergies: {allergies}
                      </Typography>
                    )}
                  </Box>
                )}
              </Box>
            </Box>

            <Box className={classes.rightSection}>
              <Box className={classes.staffSection}>
                <StaffShiftProcedureContext
                  hospitalId={hospitalId}
                  procedureId={procedure.id}
                  date={format(date, 'YYYY-MM-DD')}
                  staffShifts={procedure.staffShifts || []}
                >
                  <StaffSlotProcedureContext
                    hospitalId={hospitalId}
                    procedureId={procedure.id}
                    date={format(date, 'YYYY-MM-DD')}
                  >
                    <StaffShifts
                      staffShifts={procedure.staffShifts || []}
                      procedure={procedure}
                      isKiosk={isKiosk}
                      editableStaff={editableStaff}
                      type="ProcedureShift"
                    >
                      {canAddStaff && (
                        <StaffShiftMenu procedure={procedure} defaultPrimarySpecialty={PrimarySpecialty.OR} />
                      )}
                    </StaffShifts>
                  </StaffSlotProcedureContext>
                </StaffShiftProcedureContext>
              </Box>
              <Box px={0.5}>
                <VendorProcedureProvider procedureId={procedure.id} date={format(date, 'YYYY-MM-DD')}>
                  <Vendors procedure={procedure} editableStaff={editableStaff} vendorColor={vendorColor}>
                    {canAddStaff && <VendorMenu procedure={procedure} />}
                  </Vendors>
                </VendorProcedureProvider>
              </Box>
            </Box>
          </Box>
        </Box>
      </Paper>
    );
  };

  const renderRoom = (room: Room) => {
    if (!hospitalId) return null;

    const anesthesiologistName = fullNameToLastNameRest(room?.anesthesiologistShifts?.[0]?.anesthesiologist?.name);
    const secondaryAnesthesiologistName = fullNameToLastNameRest(
      room?.secondaryAnesthesiologistShifts?.[0]?.anesthesiologist?.name
    );
    const anesthesiologistShiftRoomId = room?.anesthesiologistShifts?.[0]?.id;
    const secondaryAnesthesiologistShiftRoomId = room?.secondaryAnesthesiologistShifts?.[0]?.id;

    return (
      <Box key={room.id || room.name} className={classes.roomSection}>
        <StaffShiftRoomContext
          hospitalId={hospitalId}
          roomId={room.id}
          date={format(date, 'YYYY-MM-DD')}
          staffShifts={room.staffShifts || []}
        >
          <StaffSlotRoomContext hospitalId={hospitalId} roomId={room.id} date={format(date, 'YYYY-MM-DD')}>
            <Box className={classes.staffWrapper}>
              <Box className={classes.roomHeader}>
                {!isAnesthesiologistSchedule && canAddStaff && (
                  <Box mr={1}>{!!room.id && <StaffShiftMenu defaultPrimarySpecialty={PrimarySpecialty.OR} />}</Box>
                )}

                <Box flex={1}>
                  <Typography variant="h5" style={{ fontWeight: 'bold' }}>
                    {room.name}
                  </Typography>
                </Box>

                {!!room.id && createProcedureScheduleModule && canUseScheduleViewProcedure && openProcedureForm && (
                  <Box mr={1}>
                    <Button
                      color="primary"
                      onClick={() => {
                        openProcedureForm(undefined, operationRooms, room, date);
                      }}
                    >
                      Add Procedure
                    </Button>
                  </Box>
                )}

                <Box mr={1}>
                  {!!room.id && (
                    <AnesthesiologistRoom
                      date={date}
                      roomId={room.id}
                      condensView={condensView}
                      editableAnesthesiologist={editableAnesthesiologist}
                      anesthesiologistName={anesthesiologistName}
                      secondaryAnesthesiologistName={secondaryAnesthesiologistName}
                      anesthesiologistShiftRoomId={anesthesiologistShiftRoomId}
                      secondaryAnesthesiologistShiftRoomId={secondaryAnesthesiologistShiftRoomId}
                      procedureQuery={room.procedures}
                      roomType={room.type}
                      secondaryRoomAnesthesiologist={secondaryRoomAnesthesiologist}
                      autoDischarge={autoDischarge}
                    />
                  )}
                </Box>
              </Box>

              {!isAnesthesiologistSchedule && (room.staffShifts || []).length > 0 && (
                <Box mb={2}>
                  <StaffShifts
                    staffShifts={room.staffShifts || []}
                    editableStaff={editableStaff}
                    isKiosk={isKiosk}
                    type="RoomShift"
                  />
                </Box>
              )}
            </Box>

            <Box>
              {room.procedures && room.procedures.length > 0 ? (
                room.procedures.map(procedure => renderProcedure(procedure, room))
              ) : (
                <Box className={classes.emptyColumn}>
                  <Typography variant="body2" color="textSecondary">
                    There are no procedures in {room.name} {isToday(date) ? 'for today' : 'on this day'}.
                  </Typography>
                </Box>
              )}
            </Box>
          </StaffSlotRoomContext>
        </StaffShiftRoomContext>
      </Box>
    );
  };

  return (
    <Box className={classes.pageContainer}>
      <Box className={classes.scrollContainer}>
        <Box className={classes.contentContainer}>
          {operationRooms.map(room => {
            if (!hospitalId) return null;
            return renderRoom(room);
          })}
        </Box>
      </Box>
    </Box>
  );
};

export default ScheduleListView;
